import { useState, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useDevices } from '@/hooks/useDevices';
import { api } from '@/lib/api';
import { Card, CardBody, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { History as HistoryIcon } from 'lucide-react';
import type { CleaningHistoryRow } from '@/lib/types';

export function HistoryPage() {
  const { data: devices } = useDevices();
  const [selected, setSelected] = useState<string>('');

  const queries = useQueries({
    queries: (devices ?? []).map((d) => ({
      queryKey: ['history', d.client_id],
      queryFn: () => api<CleaningHistoryRow[]>(`/api/devices/${d.client_id}/history`),
    })),
  });

  const rows = useMemo(() => {
    let all: CleaningHistoryRow[] = [];
    queries.forEach((q) => { if (q.data) all = all.concat(q.data); });
    if (selected) all = all.filter((r) => r.client_id === selected);
    all.sort((a, b) => (b.started_at ?? '').localeCompare(a.started_at ?? ''));
    return all;
  }, [queries, selected]);

  const loading = queries.some((q) => q.isLoading);

  return (
    <div>
      <div className="mb-5 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-ink tracking-tight">Cleaning history</h1>
          <p className="text-sm text-slate-500 mt-0.5">Every cycle started, completed, or stopped — across your devices.</p>
        </div>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm"
        >
          <option value="">All devices</option>
          {(devices ?? []).map((d) => (
            <option key={d.client_id} value={d.client_id}>{d.client_name}</option>
          ))}
        </select>
      </div>

      <Card>
        <CardBody>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<HistoryIcon className="w-7 h-7" />}
              title="No cleaning cycles yet"
              description="When a device runs a cycle, it will appear here."
            />
          ) : (
            <div className="overflow-x-auto -mx-5">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-5 py-2 text-left">Device</th>
                    <th className="px-5 py-2 text-left">Started</th>
                    <th className="px-5 py-2 text-left">Completed</th>
                    <th className="px-5 py-2 text-left">Status</th>
                    <th className="px-5 py-2 text-right">Relays</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-5 py-2.5 font-medium text-ink">{r.client_name || r.client_id}</td>
                      <td className="px-5 py-2.5 text-slate-600 tabular-nums">{r.started_at || '—'}</td>
                      <td className="px-5 py-2.5 text-slate-600 tabular-nums">{r.completed_at || '—'}</td>
                      <td className="px-5 py-2.5">
                        <Badge tone={r.status === 'completed' ? 'ok' : r.status === 'running' ? 'warn' : 'bad'}>
                          {r.status}
                        </Badge>
                      </td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-slate-600">{r.total_relays || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {!loading && rows.length > 0 && (
        <div className="mt-3 text-xs text-slate-500">
          <CardTitle className="inline">{rows.length}</CardTitle> rows
        </div>
      )}
    </div>
  );
}
